/** @jsxImportSource @emotion/react */
import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { daySwitcherStyles } from './day-switcher.styles';

type Props = {
    value: number;
    onChange: (day: number) => void;
};

const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

const clampDay = (day: number) => {
    if (Number.isNaN(day)) return 0;
    if (day < 0) return 0;
    if (day > 6) return 6;
    return day;
};

export const DaySwitcher: FC<Props> = ({ value, onChange }) => {
    const selected = clampDay(value);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const [segmentPx, setSegmentPx] = useState<number>(0);

    useLayoutEffect(() => {
        const el = trackRef.current;
        if (!el) return;

        const update = () => {
            const width = el.clientWidth;
            const innerWidth = Math.max(0, width - 8);
            setSegmentPx(innerWidth / 7);
        };

        update();

        const ro = new ResizeObserver(() => update());
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const highlightStyle = useMemo(() => {
        const circle = 38;
        const baseLeft = 4 + Math.max(0, (segmentPx - circle) / 2);
        const x = segmentPx * selected;
        return {
            left: `${baseLeft}px`,
            transform: `translateX(${x}px)`,
        };
    }, [segmentPx, selected]);

    return (
        <div css={daySwitcherStyles.root}>
            <div
                ref={trackRef}
                css={daySwitcherStyles.track}
                role="tablist"
                aria-label="Rate day selector">
                <div css={daySwitcherStyles.highlight} style={highlightStyle} aria-hidden="true" />
                {DAY_LABELS.map((label, idx) => (
                    <button
                        key={`${label}-${idx}`}
                        type="button"
                        css={daySwitcherStyles.dayButton}
                        onClick={() => onChange(idx)}
                        aria-selected={idx === selected}
                        role="tab">
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
};

